// This file defines content types and metadata that can be used in both the Edge Function environment
// and the React client environment
import { useEffect, useState } from 'react'
import { useAuth } from '@/lib/auth.tsx'
import { supabase } from '@/lib/supabase'

// Instead of importing Lucide icons directly, we'll use string identifiers
export type IconIdentifier =
    | 'message-square'
    | 'file-text'
    | 'globe'
    | 'mail'
    | 'help-circle'
    | 'video'
    | 'wrench'
    | 'layout-grid'

export interface ContentDependency {
    type: string
    description: string
    required: boolean
}

export interface RendererInfo {
    id: string;
    displayName: string;
}

// Option interface for select inputs
export interface SelectOption {
    label: string
    value: string
    icon?: string // Optional icon identifier for platform options
    showWhen?: ShowWhenCondition // Optional conditional display logic for this specific option
}

// Conditional display interface
export interface ShowWhenCondition {
    field: string           // The field name to watch (e.g., 'platform')
    values: string[]        // Array of values that should show this field (e.g., ['instagram'])
}

export interface ContentTypeMetadata {
    id: string
    title: string
    description: string
    iconName: IconIdentifier
    dependencies: ContentDependency[]
    requiredInputs: {
        name: string
        description: string
        type: 'text' | 'url' | 'select' | 'select_buttons' | 'multiselect' | 'number' | 'ideas' | 'select_slider'
        required: boolean
        options?: string[] | SelectOption[] // Can be simple strings or label-value pairs
        multipleSelections?: boolean // Whether multiple options can be selected
        placeholder?: string
        default?: any
        suggestions?: string[]
        showWhen?: ShowWhenCondition // Conditional display logic
    }[]
    status: 'LIVE' | 'COMING_SOON' | 'NOT_AVAILABLE' | 'BETA'
    requiredPlan?: 'FREE' | 'BASIC' | 'PRO' | 'ENTERPRISE'
    renderers?: {
        available: string[];
        info: RendererInfo[];
        default: string;
    }
}

// Helper function to check if an option is a SelectOption
export function isSelectOption(option: any): option is SelectOption {
    return option && typeof option === 'object' && 'label' in option && 'value' in option;
}

// Helper function to get option label
export function getOptionLabel(option: string | SelectOption): string {
    return isSelectOption(option) ? option.label : option;
}

// Helper function to get option value
export function getOptionValue(option: string | SelectOption): string {
    return isSelectOption(option) ? option.value : option;
}

// Helper function to get option icon
export function getOptionIcon(option: string | SelectOption): string | undefined {
    return isSelectOption(option) ? option.icon : undefined;
}

// Empty array for initial state before API response
export const defaultContentTypes: ContentTypeMetadata[] = []

// Current content types from the registry
export let contentTypes: ContentTypeMetadata[] = []

// Hook to fetch content types from the API
export function useContentTypes() {
    const { session } = useAuth()
    const [types, setTypes] = useState<ContentTypeMetadata[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<Error | null>(null)
    const [userPlan, setUserPlan] = useState<string>('FREE')

    useEffect(() => {
        async function fetchUserPlan() {
            if (!session) return 'FREE'

            try {
                const { data, error } = await supabase
                    .rpc('get_plan_limitations')

                if (error) {
                    console.error('Error fetching user plan:', error)
                    return 'FREE'
                }

                // The function returns plan info as JSON
                const planData = data as { plan_id: string }
                return planData?.plan_id || 'FREE'
            } catch (err) {
                console.error('Error in plan fetch:', err)
                return 'FREE'
            }
        }

        async function fetchContentTypes() {
            if (!session) {
                setLoading(false)
                return
            }

            try {
                setLoading(true)
                setError(null)

                // Get the user's current plan
                const plan = await fetchUserPlan()
                setUserPlan(plan)

                const workerUrl = import.meta.env.VITE_WORKERS_URL;
                const endpointUrl = `${workerUrl}/rainfactory/content-functions/get-content-types`

                const response = await fetch(endpointUrl, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${session.access_token}`,
                        'Content-Type': 'application/json',
                        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || '',
                    }
                })

                if (!response.ok) {
                    throw new Error(`Failed to fetch content types: ${response.status} ${response.statusText}`)
                }

                const data = await response.json()

                if (!data || !Array.isArray(data)) {
                    throw new Error('Invalid response format from content types API')
                }

                // Update access based on user's plan
                const typesWithAccess = data.map(type => {
                    // If this content type requires a plan the user doesn't have
                    if (type.requiredPlan && type.status === 'NOT_AVAILABLE') {
                        const planHierarchy = {
                            'FREE': 0,
                            'BASIC': 1,
                            'PRO': 2,
                            'ENTERPRISE': 3
                        }

                        const userPlanLevel = planHierarchy[plan as keyof typeof planHierarchy] || 0
                        const requiredPlanLevel = planHierarchy[type.requiredPlan as keyof typeof planHierarchy] || 0

                        // If user's plan level is lower than required
                        if (userPlanLevel < requiredPlanLevel) {
                            return {
                                ...type,
                                status: 'NOT_AVAILABLE',
                            }
                        }

                        // If user has appropriate plan, make it available
                        return {
                            ...type,
                            status: 'LIVE',
                        }
                    }

                    return type
                })

                // Set state and update global contentTypes
                setTypes(typesWithAccess)
                contentTypes = typesWithAccess

            } catch (err) {
                console.error('Error fetching content types:', err)
                setError(err instanceof Error ? err : new Error(String(err)))

                // Don't use default types, just keep the empty array
                setTypes([])
                contentTypes = []
            } finally {
                setLoading(false)
            }
        }

        fetchContentTypes()

        // If session changes, re-fetch the content types
        return () => {
            // Reset state when component unmounts or session changes
            contentTypes = []
        }
    }, [session])

    return { contentTypes: types, loading, error, userPlan }
} 